import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Space, Typography, Button, message, Divider, Input } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { updateGodMode } from '@redux/actions/profileActions';
import { Copy, CrownSimple } from '@assets/icons/ui';
import { isValidEmail } from '@utils/utils';
import './GodModeBanner.scss';

const { Title, Text } = Typography;

function GodModeBanner({ ie, uiDomain, auth }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const profile = useSelector(state => state.profileReducer.profile);
  const loading = useSelector(state => state.profileReducer.loading);
  const [investigationEmail, setInvestigationEmail] = useState(ie || '');
  const bannerRef = useRef(null);

  const { user_id, email } = profile || {};
  const displayEmail = email || ie;
  const investigationEmailNotValid = investigationEmail && !isValidEmail(investigationEmail);
  const isTurnOff = ie === investigationEmail || !investigationEmail;

  useEffect(() => {
    setInvestigationEmail(ie || '');
  }, [ie]);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return undefined;

    const updateBannerHeight = () => {
      document.documentElement.style.setProperty(
        '--god-mode-banner-height',
        `${banner.offsetHeight}px`
      );
    };

    updateBannerHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateBannerHeight)
      : null;

    resizeObserver?.observe(banner);
    window.addEventListener('resize', updateBannerHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateBannerHeight);
      document.documentElement.style.removeProperty('--god-mode-banner-height');
    };
  }, []);

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    message.success(t('message.copied'));
  }

  async function afterChangeGodMode() {
    try {
      await auth.renewSession();
    } catch (error) {
      console.error('renewSession failed:', error);
    }
    window.location.reload();
  }

  async function handleGodModeChange() {
    if (investigationEmailNotValid || loading) return;

    await dispatch(updateGodMode(isTurnOff ? null : investigationEmail, afterChangeGodMode));
  }

  return (
    <div className='god-mode-banner' ref={bannerRef}>
      <div className='god-mode-content'>
        <Space size={6} align='center' className='title'>
          <CrownSimple />
          <Title level={5}>{t('title.godMode')}</Title>
        </Space>

        <div className='god-mode-details'>
          <span className='god-mode-detail'>
            <Text type='secondary'>{t('title.viewingUserWithColon')} <Text className='god-mode-value'>{displayEmail}</Text></Text>
            <Copy
              className='copy-icon'
              onClick={() => copyToClipboard(displayEmail)}
            />
          </span>

          {uiDomain && (
            <span className='god-mode-detail'>
              <Divider size='small' orientation='vertical' className='god-mode-divider' />
              <Text type='secondary'>{t('title.platformWithColon')} <Text className='god-mode-value'>{uiDomain}</Text></Text>
            </span>
          )}

          {user_id && (
            <span className='god-mode-detail'>
              <Divider size='small' orientation='vertical' className='god-mode-divider' />
              <span className='god-mode-copyable'>
                <Text type='secondary'>{t('title.userIdWithColon')} <Text className='god-mode-value'>{user_id}</Text></Text>
                <Copy
                  className='copy-icon'
                  onClick={() => copyToClipboard(user_id)}
                />
              </span>
            </span>
          )}
        </div>
      </div>

      <Space size={8} align='center' className='god-mode-actions'>
        <Input
          size='small'
          disabled={loading}
          value={investigationEmail}
          onChange={({ target: { value } }) => setInvestigationEmail(value)}
          onPressEnter={handleGodModeChange}
        />
        <Button
          size='small'
          onClick={handleGodModeChange}
          loading={loading}
          disabled={investigationEmailNotValid}
        >
          {isTurnOff ? t('action.turnOff') : t('action.change')}
        </Button>
      </Space>
    </div>
  );
}

GodModeBanner.propTypes = {
  ie: PropTypes.string,
  uiDomain: PropTypes.string,
  auth: PropTypes.object.isRequired,
};

export default GodModeBanner;
